import Vue from 'vue';
import VueRouter, { NavigationGuardNext, RawLocation, Route } from 'vue-router';
import auth from '@/auth/authService';
import Admin from './Admin';
import Business from './Business';
import Candidate from './Candidate';
import { has } from 'lodash';
import store from '@/store';

Vue.use(VueRouter);

const routes = [
  // =============================================================================
  // Admin/Business/Candidate routes
  // =============================================================================
  ...Admin,
  ...Business,
  ...Candidate,
  // =============================================================================
  // Global Routes
  // =============================================================================
  {
    path: '',
    component: () => import('@/layouts/WpLayout.vue'),
    children: [
      {
        path: '',
        redirect: 'login',
      },
      {
        path: 'login',
        name: 'login',
        component: () => import('@/pages/Basic/Login.vue'),
        meta: {
          rule: 'guest',
          iframeOnly: true,
          wpUrl: process.env.VUE_APP_WP_LOGIN_URL,
        },
      },
    ],
  },
  {
    path: '/v2',
    component: () => import('@/layouts/WpLayout2.vue'),
    children: [
      {
        path: '',
        redirect: 'login',
      },
      {
        path: 'login',
        name: 'v2-login',
        component: () => import('@/pages/Basic/LoginV2.vue'),
        meta: {
          rule: 'guest',
          iframeOnly: true,
          wpUrl: process.env.VUE_APP_WP_LOGIN_URL,
        },
      },
      {
        path: 'login',
        query: { role: 'business' },
        name: 'v2-business-login',
        component: () => import('@/pages/Basic/LoginV2.vue'),
        meta: {
          rule: 'guest',
          iframeOnly: true,
          wpUrl: process.env.VUE_APP_WP_LOGIN_URL_FOR_BUSINESS,
        },
      },
      {
        path: 'login',
        query: { role: 'candidate' },
        name: 'v2-candidate-login',
        component: () => import('@/pages/Basic/LoginV2.vue'),
        meta: {
          rule: 'guest',
          iframeOnly: true,
          wpUrl: process.env.VUE_APP_WP_LOGIN_URL_FOR_CANDIDATE,
        },
      },
      {
        path: 'business-register',
        name: 'v2-business-register',
        component: () => import('@/pages/Business/BusinessRegister.vue'),
        meta: {
          rule: 'guest',
          iframeOnly: true,
          wpUrl: process.env.VUE_APP_WP_SIGN_UP_URL_FOR_BUSINESS,
        },
      },
      {
        path: 'business-account-recovery',
        name: 'v2-business-reset-password',
        component: () => import('@/pages/Business/BusinessAccountRecovery.vue'),
        meta: {
          rule: 'guest',
          iframeOnly: true,
          wpUrl: process.env.VUE_APP_WP_FORGOT_PASSWORD_URL_FOR_BUSINESS,
        },
      },
      {
        path: 'candidate-account-recovery',
        name: 'v2-candidate-reset-password',
        component: () =>
          import('@/pages/Candidate/CandidateAccountRecovery.vue'),
        meta: {
          rule: 'guest',
          iframeOnly: true,
          wpUrl: process.env.VUE_APP_WP_FORGOT_PASSWORD_URL_FOR_CANDIDATE,
        },
      },
      {
        path: 'candidate-register',
        name: 'v2-candidate-register',
        component: () => import('@/pages/Candidate/CandidateRegister.vue'),
        meta: {
          rule: 'guest',
          iframeOnly: true,
          wpUrl: process.env.VUE_APP_WP_SIGN_UP_URL_FOR_CANDIDATE,
        },
      },
    ],
  },
  {
    path: '',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        redirect: 'dashboard',
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/pages/Basic/Dashboard.vue'),
        meta: {
          rule: 'loggedIn',
          pageTitle: 'Dashboard',
        },
      },
      {
        path: 'privacy-statement',
        name: 'privacy-statement',
        component: () =>
          import('@/pages/Basic/Document/DocumentPrivacyStatement.vue'),
        meta: {
          rule: 'any',
          pageTitle: 'Privacy Statement',
        },
      },
      {
        path: 'terms-and-conditions',
        name: 'terms-and-conditions',
        component: () =>
          import('@/pages/Basic/Document/DocumentTermsAndConditions.vue'),
        meta: {
          rule: 'any',
          pageTitle: 'Terms And Conditions',
        },
      },
      {
        path: 'additional-service',
        name: 'additional-service',
        component: () =>
          import('@/pages/Basic/Document/DocumentAdditionalService.vue'),
        meta: {
          rule: 'any',
          pageTitle: 'Additional Service',
        },
      },
      {
        path: 'privacy-policy',
        name: 'privacy-policy',
        component: () =>
          import('@/pages/Basic/Document/DocumentPrivacyPolicy.vue'),
        meta: {
          rule: 'any',
          pageTitle: 'Privacy Policy',
        },
      },
    ],
  },
  {
    path: '*',
    redirect: '/404',
  },
  {
    path: '',
    component: () => import('@/layouts/ErrorLayout.vue'),
    children: [
      {
        path: '403',
        name: '403',
        component: () => import('@/pages/Basic/403.vue'),
        props: true,
        meta: {
          rule: 'any',
        },
      },
      {
        path: '404',
        name: '404',
        props: true,
        component: () => import('@/pages/Basic/404.vue'),
        meta: {
          rule: 'any',
        },
      },
      {
        path: '500',
        name: '500',
        props: true,
        component: () => import('@/pages/Basic/500.vue'),
        meta: {
          rule: 'any',
        },
      },
      {
        path: 'loading',
        name: 'loading',
        props: true,
        component: () => import('@/pages/Basic/Loading.vue'),
        meta: {
          rule: 'any',
        },
      },
      {
        path: 'error-session-time-out',
        name: 'error-session-time-out',
        props: true,
        component: () => import('@/pages/Basic/ErrorSessionTimeOut.vue'),
        meta: {
          rule: 'any',
        },
      },
      {
        path: 'error-api-request',
        name: 'error-api-request',
        props: true,
        component: () => import('@/pages/Basic/ErrorApiRequest.vue'),
        meta: {
          rule: 'any',
        },
      },
      {
        path: 'error-response-invalid',
        name: 'error-response-invalid',
        props: true,
        component: () => import('@/pages/Basic/ErrorResponseInvalid.vue'),
        meta: {
          rule: 'any',
        },
      },
      {
        path: 'error-throttle',
        name: 'error-throttle',
        props: true,
        component: () => import('@/pages/Basic/ErrorThrottle.vue'),
        meta: {
          rule: 'any',
        },
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

function inIframe() {
  if (!window.top) {
    return false;
  }
  return window.location !== window.top.location;
}

function needRedirectToWP(topLocation, wpUrl) {
  const allowWPRedirect = process.env.VUE_APP_REDIRECT_TO_WP === 'true';
  console.debug(
    'route.beforeEach ENV REDIRECT_TO_WP: ' + process.env.VUE_APP_REDIRECT_TO_WP
  );
  console.debug('route.beforeEach VAR allowWPRedirect: ' + allowWPRedirect);
  if (!allowWPRedirect) {
    return false;
  }

  // if topLocation is not found, use inIframe to decide is needed to redirect to WP or not
  if (topLocation === null) {
    console.debug('route.beforeEach topLocation is null');
    return !inIframe();
  }

  console.debug('route.beforeEach topLocation presents');
  const topLocationHref = topLocation.origin + topLocation.pathname;
  const expectedHref = wpUrl;
  console.debug('route.beforeEach TOP url: ' + topLocationHref);
  console.debug('route.beforeEach WP  url: ' + expectedHref);

  const topLocationURL = new URL(topLocationHref);
  const expectedURL = new URL(expectedHref);

  // trim the last character '/' in pathname if presents
  const needRedirect =
    topLocationURL.hostname != expectedURL.hostname ||
    topLocationURL.pathname.replace(/\/$/, '') !=
      expectedURL.pathname.replace(/\/$/, '');

  // if topLocationHref is not equals to expectedHref, then need redirect to WP
  console.debug(`route.beforeEach TOP != WP?: ${needRedirect}`);
  return needRedirect;
}

// available return value is one of ["next", "redirectToVue", "redirectToWP", "exception"]
function handleNext(to: Route): string {
  if (to.meta.rule === 'guest') {
    if (
      has(to.meta, 'iframeOnly') &&
      to.meta.iframeOnly === true &&
      !has(to.meta, 'wpUrl')
    ) {
      return 'exception';
    }

    let topLocation: Location | null = null;
    try {
      if (!window.top) {
        return 'next';
      }
      topLocation = window.top.location;
      console.debug('route.beforeEach top location:');
      console.debug(topLocation);
      if (topLocation['nodeType']) {
        topLocation = null;
        console.debug(
          'route.beforeEach NOT allow to access window.top.location'
        );
      }
    } catch (error) {
      topLocation = null;
      console.debug('route.beforeEach NOT allow to access window.top.location');
    }

    if (
      to.meta.iframeOnly === true &&
      needRedirectToWP(topLocation, to.meta.wpUrl)
    ) {
      return 'redirectToWP';
    }
  } else {
    if (inIframe()) {
      return 'redirectToVue';
    }
  }

  return 'next';
}

function redirectToVue(next) {
  next({ name: 'loading' });
  if (window.top) {
    window.top.location.href = window.location.href;
  }
}

function redirectToWP(next, wpUrl) {
  console.debug('route.beforeEach perform WP redirect');
  next({ name: 'loading' });
  if (window.top) {
    window.top.location.href = wpUrl;
  }
}

function getRouteByName(routeName): Route {
  const activeRoleKey = localStorage.getItem('active_role_key');

  switch (activeRoleKey) {
    case 'rAdmin':
      routeName = 'administrator-' + routeName;
      break;
    case 'rBusiness':
      routeName = 'business-' + routeName;
      break;
    case 'rCandidate':
      routeName = 'candidate-' + routeName;
      break;
    default:
      break;
  }

  const resolved = router.resolve({
    name: routeName,
  });

  return resolved.route;
}

// let swUpdateHasFirstCheck = false

// function swUpdateFirstCheck() {
//   if (! swUpdateHasFirstCheck && process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
//     console.debug('invoke swUpdateFirstCheck')
//     swUpdateHasFirstCheck = true
//     navigator.serviceWorker.getRegistration().then(async function (registration) {
//       if (! registration) {
//         return
//       }

//       console.debug('Route first update. Trigger update.');
//       await registration.update().then(function () {
//         if (registration.waiting) {
//           console.debug('Rotue first update and post SKIP_WAITING')
//           registration.waiting.postMessage({ type: 'SKIP_WAITING' })
//         } else {
//           console.debug('Rotue first update waiting is null')
//         }
//       });
//     });
//   }
// }

router.beforeEach(async (to: Route, from: Route, next: NavigationGuardNext) => {
  // swUpdateFirstCheck()
  console.debug('router.beforeEach');
  console.debug('router.beforeEach from:');
  console.debug(from);
  console.debug('router.beforeEach to:');
  console.debug(to);

  if (!to.meta.rule || to.meta.rule == 'any') {
    return next();
  }

  const isAuthenticated = await auth.isAuthenticated();
  console.debug('router.beforeEach isAuthenticated: ' + isAuthenticated);
  if (!isAuthenticated) {
    // when a user is not authenticated, and the user want to access the non-guest rule page (except business activate page)
    // redirect route to the WP login page
    if (to.meta.rule != 'guest') {
      console.debug('router.beforeEach NOT AUTHENTICATED');
      const login = getRouteByName('login');
      return next(login as RawLocation);
    }
  } else {
    console.debug('router.beforeEach AUTHENTICATED');
    // when a user has been authenticated, and the user want to access the 'guest' rule page
    // redirect route to the dashboard
    if (to.meta.rule == 'guest') {
      console.debug(
        'router.beforeEach want to access guest page but redirect to dashboard page'
      );
      const dashboard = getRouteByName('dashboard');
      return next(dashboard as RawLocation);
    }
  }

  const action = handleNext(to);
  console.debug('router.beforeEach action: ' + action);

  switch (action) {
    case 'redirectToVue':
      redirectToVue(next);
      break;
    case 'redirectToWP': {
      const wpUrl = new URL(to.meta.wpUrl);
      if (Object.keys(to.query).length > 0) {
        const queryStringObj = new URLSearchParams(
          to.query as Record<string, string>
        );
        for (const [key, value] of queryStringObj) {
          wpUrl.searchParams.append.call(wpUrl.searchParams, key, value);
        }
      }
      redirectToWP(next, wpUrl.toString());
      break;
    }
    case 'exception':
      next({ name: '500' });
      break;
    case 'next':
    default:
      next();
      break;
  }
});

router.afterEach(() => {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // if the broswer support the service worker
    navigator.serviceWorker.getRegistration().then(function(registration) {
      if (!registration) {
        return;
      }
      console.log('Service Worker Registered. Trigger update.');
      console.log('swUpdateCheckCount: ' + store.state.swUpdateCheckCount);
      registration.update().then(function() {
        console.log('route after update Promise');
        store.commit('SW_UPDATE_INVOKED');
        console.log('swUpdateCheckCount: ' + store.state.swUpdateCheckCount);
      });
    });
  }
});

export default router;
